import React, { useState } from 'react';
import TypeSelector from '../../components/gallery/TypeSelector';
import SubTypeSelector from '../../components/gallery/SubTypeSelector';
import UploadArea from '../../components/gallery/UploadArea';
import { galleryAPI } from '../../services/galleryApi';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';

const AddNewGallery: React.FC = () => {
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [selectedSubTypeId, setSelectedSubTypeId] = useState<string | null>(null);
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [description, setDescription] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const navigate = useNavigate();

  const handleFilesChange = (files: File[]) => {
    setFilesToUpload(files);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTypeId) {
      toast.error('Veuillez sélectionner un type de service.');
      return;
    }
    if (filesToUpload.length === 0) {
      toast.error('Veuillez ajouter au moins une image.');
      return;
    }

    setIsUploading(true);
    setUploadSuccess(false);
    const formData = new FormData();
    formData.append('serviceType', selectedTypeId);
    if (selectedSubTypeId) {
      formData.append('serviceSubType', selectedSubTypeId);
    }
    formData.append('description', description);

    filesToUpload.forEach((file) => {
      formData.append('images', file);
    });

    try {
      await galleryAPI.uploadImages(formData);
      toast.success('Images téléchargées avec succès !');
      setUploadSuccess(true);
      // Optionally reset form fields
      setSelectedTypeId(null);
      setSelectedSubTypeId(null);
      setFilesToUpload([]);
      setDescription('');
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Erreur lors du téléchargement des images.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-6">Ajouter de nouvelles images à la galerie</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700 mb-2">Type de service <span className="text-red-500">*</span></label>
            <TypeSelector
              selectedTypeId={selectedTypeId}
              onSelectType={(id) => {
                setSelectedTypeId(id);
                setSelectedSubTypeId(null); // Reset sub-type when type changes
              }}
              allowEmpty={true}
            />
          </div>

          <div>
            <label htmlFor="serviceSubType" className="block text-sm font-medium text-gray-700 mb-2">Sous-type de service</label>
            <SubTypeSelector
              serviceTypeId={selectedTypeId}
              selectedSubTypeId={selectedSubTypeId}
              onSelectSubType={setSelectedSubTypeId}
              allowEmpty={true}
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">Description (optionnel)</label>
            <textarea
              id="description"
              rows={3}
              className="textarea textarea-bordered w-full"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ajouter une description pour ces images..."
            ></textarea>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Images <span className="text-red-500">*</span></label>
            <UploadArea onFilesChange={handleFilesChange} />
          </div>

          <button
            type="submit"
            className="btn btn-primary w-full"
            disabled={isUploading || filesToUpload.length === 0 || !selectedTypeId}
          >
            {isUploading ? 'Téléchargement...' : 'Télécharger les images'}
          </button>
        </form>

        {uploadSuccess && (
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/tech/gallery-viewer')}
              className="btn btn-success"
            >
              Voir dans la galerie
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AddNewGallery;
